'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';

/**
 * H2 — the last error boundary, and the only place a root-layout crash can be
 * reported from.
 *
 * Next's App Router routes a rendering error that no nearer `error.tsx` catches
 * to this file. Without it the browser shows Next's own built-in fallback —
 * unstyled English "Application error: a client-side exception has occurred" —
 * and the SDK never hears about the error at all, because React swallowed it
 * inside its own boundary. That is the exact class of failure this tranche
 * exists to make visible.
 *
 * `captureException` is a no-op when `Sentry.init` was never called (no DSN, or
 * a non-production build), so this file costs nothing with Sentry switched off.
 *
 * Deliberately plain. It replaces the entire document — `global-error.tsx` must
 * render its own `<html>` and `<body>` — which means the root layout's fonts,
 * providers and i18next are all gone by the time it paints. So: hardcoded
 * Hebrew like `/offline`, literal colours rather than CSS custom properties
 * (the token stylesheet may not have loaded), and no imports beyond React and
 * Sentry. Anything cleverer risks failing to render on the one page whose whole
 * job is to render when everything else did not.
 *
 * ⚠️ NOT verified in a browser — no `next build` was possible in the session
 * that added it (a sibling dev server owns `.next`). The reporting path is
 * unit-tested; the layout is not.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <html lang="he" dir="rtl">
      <body style={{ margin: 0, background: '#ffffff', color: '#141414' }}>
        <main
          style={{
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '1rem',
            padding: '2rem',
            textAlign: 'center',
            fontFamily: 'system-ui, sans-serif',
          }}
        >
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, margin: 0 }}>משהו השתבש</h1>
          <p style={{ margin: 0, color: '#5a5a5a', maxWidth: '32rem' }}>
            אירעה שגיאה בלתי צפויה. הדיווח נשלח אלינו אוטומטית. אפשר לנסות שוב.
          </p>
          <button
            type="button"
            onClick={() => reset()}
            style={{
              border: 'none',
              borderRadius: 999,
              padding: '0.75rem 1.75rem',
              background: '#141414',
              color: '#ffffff',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            נסו שוב
          </button>
        </main>
      </body>
    </html>
  );
}
